/**
 * Attendance admin API layer for vsdigiworldclient.
 *
 * These modules were ported from the standalone `attendanceadmin` app (Vue 3 +
 * PrimeVue) into this Vue 2 + Bootstrap client. They talk to the SAME shared
 * backend, reusing this app's global axios instance (`window.axios`) so the
 * bearer token + `projectid` header are attached automatically.
 *
 * The backend wraps every payload as { success, message, data }; the helpers
 * below unwrap `data` so callers never deal with the envelope — matching the
 * original service.
 */

const ADMIN_PREFIX = 'api/attendance/admin'

function get(url, params) {
	return window.axios.get(url, { params }).then((r) => r.data.data)
}

function post(url, body) {
	return window.axios.post(url, body).then((r) => r.data.data)
}

function put(url, body) {
	return window.axios.put(url, body).then((r) => r.data.data)
}

/**
 * Pull a readable message out of a Laravel error response.
 * 422 validation bags are preferred over the generic status text.
 */
export function errorMessage(error, fallback = 'Something went wrong') {
	const data = error && error.response && error.response.data
	if (data) {
		if (data.errors) {
			const first = Object.values(data.errors)[0]
			if (Array.isArray(first) && first[0]) return first[0]
		}
		if (typeof data.message === 'string' && data.message) return data.message
	}
	return fallback
}

// ---------------------------------------------------------------------------
// Dashboard and reports
// ---------------------------------------------------------------------------

export const reportsService = {
	dashboard: (date) => get(`${ADMIN_PREFIX}/dashboard`, { date }),

	monthly: (month, policyId) =>
		get(`${ADMIN_PREFIX}/reports/monthly`, { month, policy_id: policyId }),

	/** Absolute URL for the CSV hand-off to payroll. */
	monthlyCsvUrl: (month) =>
		`${window.axios.defaults.baseURL || ''}${ADMIN_PREFIX}/reports/monthly.csv${
			month ? `?month=${month}` : ''
		}`,

	overtime: (params) => get(`${ADMIN_PREFIX}/reports/overtime`, params),

	leaves: (params) => get(`${ADMIN_PREFIX}/reports/leaves`, params),
}

// ---------------------------------------------------------------------------
// The daily register
// ---------------------------------------------------------------------------

export const registerService = {
	daily: (params) => get(`${ADMIN_PREFIX}/register/daily`, params),

	employeeMonth: (employeeId, month) =>
		get(`${ADMIN_PREFIX}/register/employee/${employeeId}`, { month }),

	/** Replay the engine. Safe to run repeatedly; locked periods are skipped. */
	recompute: (body) => post(`${ADMIN_PREFIX}/register/recompute`, body),

	manualPunch: (body) => post(`${ADMIN_PREFIX}/register/manual-punch`, body),

	locks: () => get(`${ADMIN_PREFIX}/register/locks`),

	lock: (body) => post(`${ADMIN_PREFIX}/register/lock`, body),

	unlock: (id) => window.axios.delete(`${ADMIN_PREFIX}/register/lock/${id}`),
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export const employeeService = {
	list: (params) => get(`${ADMIN_PREFIX}/employees`, params),

	show: (id) => get(`${ADMIN_PREFIX}/employees/${id}`),

	update: (id, body) => put(`${ADMIN_PREFIX}/employees/${id}`, body),

	resetDevice: (id) => post(`${ADMIN_PREFIX}/employees/${id}/reset-device`),

	candidates: () => get(`${ADMIN_PREFIX}/employees/candidates`),

	enroll: (body) => post(`${ADMIN_PREFIX}/employees/enroll`, body),

	bulkAssign: (body) => post(`${ADMIN_PREFIX}/employees/bulk-assign`, body),
}

// ---------------------------------------------------------------------------
// Master data — all five master tables share the same CRUD shape.
// ---------------------------------------------------------------------------

function crud(resource) {
	return {
		list: (params) => get(`${ADMIN_PREFIX}/${resource}`, params),
		create: (body) => post(`${ADMIN_PREFIX}/${resource}`, body),
		update: (id, body) => put(`${ADMIN_PREFIX}/${resource}/${id}`, body),
		remove: (id) => window.axios.delete(`${ADMIN_PREFIX}/${resource}/${id}`),
	}
}

export const locationService = {
	...crud('locations'),
	/** Regenerate a site's QR token, invalidating earlier printouts. */
	rotateToken: (id) => post(`${ADMIN_PREFIX}/locations/${id}/qr/rotate`),
}
export const shiftService = crud('shifts')
export const holidayService = crud('holidays')
export const leaveTypeService = crud('leave-types')

export const deductionTypeService = crud('deduction-types')

/**
 * Money and paid days charged against a day. Deletion is a cancellation on the
 * server — the row stays visible, since this is money against a named person.
 */
export const deductionService = {
	list: (params) => get(`${ADMIN_PREFIX}/deductions`, params),
	create: (body) => post(`${ADMIN_PREFIX}/deductions`, body),
	update: (id, body) => put(`${ADMIN_PREFIX}/deductions/${id}`, body),
	remove: (id, reason) =>
		window.axios.delete(`${ADMIN_PREFIX}/deductions/${id}`, { data: { reason } }),
}

export const performanceService = {
	list: (date, employeeId) =>
		get(`${ADMIN_PREFIX}/performance`, { date, employee_id: employeeId || undefined }),
	/** Totals per employee for from..to, with the targets for those days. */
	listRange: (fromDate, toDate, employeeId) =>
		get(`${ADMIN_PREFIX}/performance`, { from_date: fromDate, to_date: toDate, employee_id: employeeId || undefined }),
	save: (body) => post(`${ADMIN_PREFIX}/performance`, body),
}

export const policyService = {
	...crud('policies'),
	/** Company defaults, the policy's overrides, and what they resolve to. */
	effective: (id) => get(`${ADMIN_PREFIX}/policies/${id}/effective`),
}

// ---------------------------------------------------------------------------
// Approvals and settings
// ---------------------------------------------------------------------------

export const approvalService = {
	list: (params) => get(`${ADMIN_PREFIX}/approvals`, params),

	decide: (id, approved, note) =>
		post(`${ADMIN_PREFIX}/approvals/${id}/decide`, { approved, note }),

	runAuto: () => post(`${ADMIN_PREFIX}/approvals/run-auto`),
}

export const settingsService = {
	get: () => get(`${ADMIN_PREFIX}/settings`),

	update: (section, value) => put(`${ADMIN_PREFIX}/settings`, { section, value }),
}
